import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CallToActionCard from "../../components/CallToActionCard";
import LoadingIndicator from "../../components/LoadingIndicator";
import FlatList from "../../components/FlatList";
import useThemeHook from "../../hooks/useThemeHook";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import { User } from "../../../types";
import { UserCard } from "../../components/CryptoAssetsSection";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import CommonNavBar from "../../components/CommonNavBar";
import CommonPageTitle from "../../components/CommonPageTitle";
import Text from "../../components/ThemedText";
import { THEME_COLORS } from "../../themes/DarkLight";
import { FONTSIZE } from "../../../constants";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import ReferralMilestones from "../../components/ReferralMilestones";
export default function ReferralsPage() {
  const getTranslationText = useTranslationHook();

  var data = useSelector((state: any) => state.userReferrals);
  const actualUser = useSelector((state: any) => state.actualUser);

  const dispatch = useDispatch();
  const [noMore, setNoMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<any>(null);

  const getPosts = async () => {
    try {
      if (isLoading || noMore || actualUser === null) {
        return;
      }
      setIsLoading(true);
      if (data.length !== 0) {
        setIsLoading(false);
        return;
      }
      const {
        referrals: newData,
        lastDoc: newLastDoc,
        hasMore,
      } = await arthereumProvider.getUserReferrals(actualUser?.user?.id, null);
      dispatch(actions.setUserReferrals(newData));
      setLastDoc(newLastDoc);
      setNoMore(!hasMore);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPosts();
  }, []);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = async () => {
    try {
      if (isRefreshing) {
        return;
      }
      setIsRefreshing(true);
      const {
        referrals: newData,
        lastDoc: newLastDoc,
        hasMore,
      } = await arthereumProvider.getUserReferrals(actualUser?.user?.id, null);
      dispatch(actions.setUserReferrals(newData));
      setLastDoc(newLastDoc);
      setNoMore(!hasMore);
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const navigate = useNavigate();

  const loadMore = async () => {
    try {
      if (isLoadingMore || noMore) {
        return;
      }
      setIsLoadingMore(true);
      const {
        referrals: newData,
        lastDoc: newLastDoc,
        hasMore,
      } = await arthereumProvider.getUserReferrals(
        actualUser?.user?.id,
        lastDoc
      );
      dispatch(actions.setUserReferrals([...data, ...newData]));
      setLastDoc(newLastDoc);
      setNoMore(!hasMore);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array ensures effect runs only once on mount

  const renderItems = ({ item, index }: { item: User; index: number }) => {
    return (
      <div
        onClick={() => {
          emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
            modal: { name: "userModal", modalData: { user: item } },
          });
        }}
        style={{ padding: "13px", paddingBottom: "5px", paddingTop: 0 }}
      >
        <div
          style={{
            backgroundColor: THEME_COLORS[theme].component_background_color,
            borderRadius: "12px",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            marginBottom: "0px",
            transition:
              "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div style={{ marginRight: "15px" }}>
            <img
              alt=""
              src={item?.profileImage}
              style={{
                objectFit: "cover",
                height: "80px",
                width: "80px",
                borderRadius: "10px",
                backgroundColor: THEME_COLORS[theme].gray,
              }}
            />
          </div>
          <div>
            <Text
              style={{ fontSize: FONTSIZE.TERTIARY, fontWeight: "500" }}
              text={item.username}
            />
            <Text
              style={{ fontSize: FONTSIZE.QUATERNARY }}
              text={item.arthereumBalance + " Arthereum"}
            />
          </div>
        </div>
      </div>
    );
  };
  const theme = useThemeHook();

  return (
    <>
      <CommonNavBar />
      <FlatList
        itemIdExtractor={(item: User) => item?.id}
        data={data}
        itemsClassName="User"
        onEndReached={loadMore}
        onRefresh={refresh}
        pageKey="referrals-page"
        renderItems={renderItems}
        listHeaderComponents={
          <>
            <CommonPageTitle
              padding={13}
              title={getTranslationText("friends_you_invited")}
            />
            
            <ReferralMilestones referralCount={actualUser?.user?.referalsCount || 0} />

            <div
              style={{
                display: false ? "flex" : "none",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoadingIndicator />
            </div>

            {isLoading && <LoadingIndicator />}
            {data.length === 0 && !isLoading && noMore && (
              <CallToActionCard
                buttonText={getTranslationText("invite")}
                description={getTranslationText(
                  "invite_friends_and_family_description"
                )}
                onClick={() => navigate("/ConnectionsPage")}
                title={getTranslationText("invite_friends_and_family")}
              />
            )}
          </>
        }
        listFooterComponents={
          <>
            {isLoadingMore && <LoadingIndicator />}
            <div style={{ padding: "60px" }}></div>
          </>
        }
      />
    </>
  );
}
